/* eslint-disable react/no-danger */
/* eslint-disable react/forbid-prop-types */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FiChevronLeft,
  FiChevronRight,
  FiMapPin,
  FiMaximize2,
  FiMoon,
  FiSend,
} from 'react-icons/fi';
import { MdOutlineHotel } from 'react-icons/md';
import PackageImage from '../common/PackageImage';
import { PackageLightbox } from './PackageGallery';
import { formatIsoDate, packageHotelImages } from '../../utils/packageUtils';
import { sanitizeHtml } from '../../utils/safeHtml';

/**
 * A hotel's photos: a small carousel in the card (arrows + counter when there
 * is more than one) that opens the full-screen viewer at the current photo.
 */
function HotelMedia({ images, name }) {
  const [index, setIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const count = images.length;

  if (!count) {
    return (
      <div className="pkg-hotel__media">
        <PackageImage alt={name} />
      </div>
    );
  }

  const step = (e, by) => {
    e.stopPropagation(); // the arrows sit on the tile, which opens the viewer
    setIndex((i) => (i + by + count) % count);
  };
  const current = images[index] || images[0];

  return (
    <>
      <div className="pkg-hotel__media pkg-hotel__media--carousel">
        <button
          type="button"
          className="pkg-hotel__media-open"
          onClick={() => setViewerOpen(true)}
          aria-label={`Open photos of ${name || 'this hotel'}`}
        >
          {/* keyed so a photo that failed to load does not hide the next one */}
          <PackageImage key={current.url} src={current.url} alt={name} />
          <span className="pkg-hotel__media-zoom" aria-hidden>
            <FiMaximize2 />
          </span>
        </button>
        {count > 1 && (
          <>
            <button
              type="button"
              className="pkg-hotel__media-nav pkg-hotel__media-nav--prev"
              onClick={(e) => step(e, -1)}
              aria-label="Previous photo"
            >
              <FiChevronLeft />
            </button>
            <button
              type="button"
              className="pkg-hotel__media-nav pkg-hotel__media-nav--next"
              onClick={(e) => step(e, 1)}
              aria-label="Next photo"
            >
              <FiChevronRight />
            </button>
            <span className="pkg-hotel__media-count">
              {index + 1} / {count}
            </span>
          </>
        )}
      </div>
      {viewerOpen && (
        <PackageLightbox
          images={images}
          startIndex={index}
          title={name}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </>
  );
}

HotelMedia.propTypes = {
  images: PropTypes.array.isRequired,
  name: PropTypes.string,
};

HotelMedia.defaultProps = {
  name: '',
};

/** Hotels for the chosen grade ("or similar" — the supplier confirms the property). */
export function PackageHotels({ hotels }) {
  if (!hotels.length) {
    return (
      <p className="pkg-section__empty">
        Hotel details will be shared by the supplier.
      </p>
    );
  }
  return (
    <div className="pkg-hotels">
      {hotels.map((h, i) => {
        const images = packageHotelImages(h);
        const info = sanitizeHtml(h.infoHtml);
        const stay = [formatIsoDate(h.checkIn), formatIsoDate(h.checkOut)]
          .filter(Boolean)
          .join(' → ');
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div className="pkg-hotel" key={`${h.hotelNames}-${i}`}>
            <HotelMedia images={images} name={h.hotelNames || ''} />
            <div className="pkg-hotel__body">
              <strong className="pkg-hotel__name">
                <MdOutlineHotel /> {h.hotelNames || '—'}
              </strong>
              <span className="pkg-hotel__meta">
                {(h.city || h.country) && (
                  <span>
                    <FiMapPin />{' '}
                    {[h.city, h.country].filter(Boolean).join(', ')}
                  </span>
                )}
                {Number(h.nights) > 0 && (
                  <span>
                    <FiMoon /> {h.nights} night
                    {Number(h.nights) === 1 ? '' : 's'}
                  </span>
                )}
                {stay && <span>{stay}</span>}
              </span>
              {info && (
                <div
                  className="pkg-html pkg-hotel__info"
                  dangerouslySetInnerHTML={{ __html: info }}
                />
              )}
            </div>
          </div>
        );
      })}
      <p className="pkg-section__note">
        Hotels are &quot;or similar&quot;: the supplier confirms the exact
        property with your booking.
      </p>
    </div>
  );
}

PackageHotels.propTypes = {
  hotels: PropTypes.array.isRequired,
};

/** Flight legs for the chosen joining city. Timings are indicative. */
export function PackageFlights({ flights }) {
  if (!flights.length) {
    return (
      <p className="pkg-section__empty">
        No flights are listed for this joining city.
      </p>
    );
  }
  return (
    <div className="pkg-flights">
      {flights.map((f, i) => {
        const outbound = String(f.legGroup || '').toLowerCase() === 'outbound';
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div className="pkg-flight" key={`${f.fromCity}-${f.toCity}-${i}`}>
            <span className="pkg-flight__icon" aria-hidden>
              <FiSend />
            </span>
            <div className="pkg-flight__route">
              <strong>
                {f.fromCity || '—'} → {f.toCity || '—'}
              </strong>
              <small>
                {[f.airline, f.flightNo].filter(Boolean).join(' · ')}
                {f.legGroup && (
                  <span className="pkg-chip">
                    {outbound ? 'Outbound' : 'Return'}
                  </span>
                )}
              </small>
            </div>
            <div className="pkg-flight__time">
              <span>
                {formatIsoDate(f.depDate)} {f.depTime || ''}
              </span>
              <small>
                Arrives {formatIsoDate(f.arrDate)} {f.arrTime || ''}
              </small>
            </div>
          </div>
        );
      })}
      <p className="pkg-section__note">
        Flight timings are the supplier&apos;s indicative schedule and may
        change.
      </p>
    </div>
  );
}

PackageFlights.propTypes = {
  flights: PropTypes.array.isRequired,
};
